import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { hasPermission, canModifyLead } from '../../utils/permissions';
import { SharedTable } from '../common';

const LeadsTableWithShared = ({
  leads,
  selectedLeads,
  setSelectedLeads,
  selectAll,
  setSelectAll,
  handleSelectAll,
  dealCounts,
  setSelectedLead,
  setViewModalOpen,
  setEmailModalOpen,
  sortBy,
  sortOrder,
  users,
  setLeads,
  handleSort,
  showColumnModal,
  setShowColumnModal,
  pagination,
  onPageChange,
  onLimitChange, // <<< Add onLimitChange prop here
  // fetchLeads, // Removed fetchLeads prop
  loading = false
}) => {
  // State to track the last called lead
  const [lastCalledLeadId, setLastCalledLeadId] = useState(null);
  // Column configuration with default order and width
  const defaultColumns = [
    { id: 'first_name', label: 'First Name', visible: true, sortable: true, width: 150 },
    { id: 'last_name', label: 'Last Name', visible: true, sortable: true, width: 150 },
    { id: 'name', label: 'Full Name', visible: false, sortable: true, width: 200 },
    { id: 'company', label: 'Company', visible: true, sortable: true, width: 180 }, // Added Company
    { id: 'role', label: 'Role', visible: true, sortable: true, width: 150 }, // Added Role
    { id: 'email', label: 'Email', visible: true, sortable: true, width: 220 },
    { id: 'secondary_email', label: 'Secondary Email', visible: false, sortable: true, width: 220 },
    { id: 'phone', label: 'Phone', visible: true, sortable: true, width: 150 },
    { id: 'secondary_phone', label: 'Secondary Phone', visible: false, sortable: true, width: 150 },
    { id: 'call_attempts', label: 'Call Attempts', visible: true, sortable: true, width: 120 },
    { id: 'status', label: 'Status', visible: true, sortable: true, width: 120 },
    { id: 'country', label: 'Country', visible: true, sortable: true, width: 120 },
    { id: 'deals', label: 'Associated Deals', visible: true, width: 120 },
    { id: 'subscribed', label: 'Subscribed', visible: true, width: 100 },
    { id: 'original_source', label: 'Original Source', visible: true, sortable: true, width: 150 },
    { id: 'latest_source', label: 'Latest Source', visible: true, sortable: true, width: 150 },
    { id: 'assigned_to', label: 'Assigned To', visible: true, sortable: true, width: 150 },
    { id: 'last_worked_date', label: 'Last Worked', visible: true, sortable: true, width: 150 },
    { id: 'created_at', label: 'Created', visible: true, sortable: true, width: 130 },
    { id: 'reconversion_date', label: 'Reconversion', visible: true, sortable: true, width: 150 },
    { id: 'last_conversion_date', label: 'Last Conversion', visible: true, sortable: true, width: 150 }
  ];

  // Load column order from localStorage or use default, ensuring new columns are added
  const [columns, setColumns] = useState(() => {
    const savedColumnsString = localStorage.getItem('leadsTableColumns');
    let savedColumns = [];
    try {
      savedColumns = savedColumnsString ? JSON.parse(savedColumnsString) : [];
    } catch (e) {
      console.error("Error parsing saved columns from localStorage", e);
      savedColumns = []; // Fallback to empty array on error
    }

    // Check if we have a valid saved order
    const hasSavedOrder = savedColumns.length > 0;
    
    // If we have a valid saved order, use it directly (ensuring all default columns are included)
    if (hasSavedOrder) {
      console.log("Using saved column order from localStorage");
      
      // Create a set of IDs from saved columns for quick lookup
      const savedColumnIds = new Set(savedColumns.map(col => col.id));
      
      // Create a map of saved columns by ID for quick property lookup
      const savedColumnsMap = new Map(savedColumns.map(col => [col.id, col]));
      
      // First, use saved columns as-is (preserving order)
      const result = [...savedColumns];
      
      // Then, add any default columns not in saved list (new columns since last save)
      // Append them at the end
      const newColumns = defaultColumns
        .filter(defaultCol => !savedColumnIds.has(defaultCol.id))
        .map(defaultCol => ({ ...defaultCol }));
      
      if (newColumns.length > 0) {
        console.log(`Adding ${newColumns.length} new columns to saved configuration`);
        result.push(...newColumns);
      }
      
      // Ensure all columns have current properties from defaultColumns
      // This makes sure label changes, etc. are picked up
      result.forEach(col => {
        const defaultCol = defaultColumns.find(d => d.id === col.id);
        if (defaultCol) {
          col.label = defaultCol.label;
          col.sortable = defaultCol.sortable;
          // Keep saved visibility and width
        }
      });
      
      // Filter out any saved columns that are no longer in the defaults
      const defaultColumnIds = new Set(defaultColumns.map(col => col.id));
      const finalResult = result.filter(col => defaultColumnIds.has(col.id));
      
      return finalResult;
    } 
    // Otherwise, use the default order
    else {
      console.log("No saved columns found, using defaults");
      return [...defaultColumns]; // Return a copy to avoid mutation
    }
  });

  // Save column configuration to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('leadsTableColumns', JSON.stringify(columns));
  }, [columns]);

  // Handle individual lead selection
  const handleLeadSelection = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
      setSelectAll(false);
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
      if (selectedLeads.length + 1 === leads.length) {
        setSelectAll(true);
      }
    }
  };

  // Render cell content based on column ID
  const renderCell = (lead, columnId) => {
    switch (columnId) {
      case 'first_name':
        return (
          <div 
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer truncate max-w-xs"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling to parent row
              setSelectedLead(lead);
              setViewModalOpen(true);
            }}
            title="Click to view or edit lead details"
          >
            {lead.first_name || '—'}
          </div>
        );
      case 'last_name':
        return (
          <div 
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer truncate max-w-xs"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling to parent row
              setSelectedLead(lead);
              setViewModalOpen(true);
            }}
            title="Click to view or edit lead details"
          >
            {lead.last_name || '—'}
          </div>
        );
      case 'name':
        return (
          <div 
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer truncate max-w-xs"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling to parent row
              setSelectedLead(lead);
              setViewModalOpen(true);
            }}
            title="Click to view or edit lead details"
          >
            {lead.name}
          </div>
        );
      case 'company': // Added case for company
        return (
          <div className="text-sm text-gray-500 truncate max-w-[180px]" title={lead.company}>
            {lead.company || '—'}
          </div>
        );
      case 'role': // Added case for role
        return (
          <div className="text-sm text-gray-500 truncate max-w-[150px]" title={lead.role}>
            {lead.role || '—'}
          </div>
        );
      case 'subscribed':
        return (
          <div className="text-sm">
            {lead.subscribed ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Yes
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                No
              </span>
            )}
          </div>
        );
      case 'email':
        return (
          <div className="text-sm text-gray-500 flex items-center">
            {lead.email && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLead(lead);
                  setEmailModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700 mr-2"
                title="Send email to this lead"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </button>
            )}
            {lead.email || '—'}
          </div>
        );
      case 'secondary_email':
        return (
          <div className="text-sm text-gray-500 flex items-center">
            {lead.secondary_email && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Create a temporary lead object with the secondary email as primary for the email modal
                  const tempLead = {...lead, email: lead.secondary_email};
                  setSelectedLead(tempLead);
                  setEmailModalOpen(true);
                }}
                className="text-blue-500 hover:text-blue-700 mr-2"
                title="Send email to secondary address"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </button>
            )}
            {lead.secondary_email || '—'}
          </div>
        );
      case 'phone':
        return (
          <div className="text-sm text-gray-500 flex items-center">
            {lead.phone && (
              <a 
                href={`tel:${lead.phone}`} 
                className="text-blue-500 hover:text-blue-700 mr-2" 
                title="Call this number (will increment call attempts)"
                onClick={async (e) => {
                  // Don't prevent default so the tel: link still works
                  
                  // Set this lead as the last called lead (for highlighting)
                  setLastCalledLeadId(lead.id);
                  
                  // Store original lead state for potential revert
                  const originalLead = { ...lead }; 

                  try {
                    // Only increment if not already at max
                    if ((lead.call_attempts || 0) < 10) {
                      const token = localStorage.getItem('token');
                      
                      // --- Optimistic UI Update ---
                      const optimisticDate = new Date().toISOString();
                      const optimisticLeads = leads.map(l =>
                        l.id === lead.id
                          ? {
                              ...l,
                              call_attempts: (l.call_attempts || 0) + 1, // Optimistically increment
                              last_worked_date: optimisticDate // Optimistically set date
                            }
                          : l
                      );
                      setLeads(optimisticLeads);
                      // --- End Optimistic UI Update ---

                      const res = await axios.patch(
                        `/api/leads/${lead.id}/call-attempts`,
                        { increment: true },
                        {
                          headers: {
                            'Content-Type': 'application/json',
                            'x-auth-token': token
                          }
                        }
                      );
                      
                      // We know the PATCH succeeded, so the call_attempts increment is confirmed.
                      // The API response (res.data) might not contain the full updated lead or the last_worked_date.
                      
                      const confirmedAttempts = (originalLead.call_attempts || 0) + 1;
                      let confirmedStatus = originalLead.status;
                      let statusUpdated = false;

                      // Check if we need to update status to "unreachable" (if call attempts reaches 7)
                      if (confirmedAttempts >= 7 && originalLead.status !== 'unreachable') {
                        confirmedStatus = 'unreachable';
                        statusUpdated = true;
                        // Update the lead status to unreachable via a separate API call
                        // We assume this PUT call also updates last_worked_date on the backend
                        await axios.put(
                          `/api/leads/${lead.id}`,
                          { status: 'unreachable' }, // Only send the status change
                          { /* headers */ }
                        ).catch(statusErr => {
                           // Handle potential error during status update if necessary
                           console.error('Error updating lead status to unreachable:', statusErr);
                           // Decide if we should revert status optimistically? For now, we'll proceed.
                        });
                      }
                      
                      // Construct the final state update based on confirmed changes & optimistic date
                      const finalUpdatedLeads = leads.map(l => {
                        if (l.id === lead.id) {
                          // Use the original lead data as base, apply confirmed changes and optimistic date
                          return { 
                            ...originalLead, // Start with original data
                            call_attempts: confirmedAttempts, // Apply confirmed attempts
                            status: confirmedStatus, // Apply confirmed status (might be original or 'unreachable')
                            last_worked_date: optimisticDate // Keep the optimistic date
                          };
                        }
                        return l;
                      });
                      setLeads(finalUpdatedLeads); // Apply the final calculated state
                    }
                  } catch (err) {
                    console.error('Error updating call attempts:', err);
                    // --- Revert Optimistic Update on Error ---
                    const revertedLeads = leads.map(l => 
                      l.id === lead.id ? originalLead : l // Restore original lead data
                    );
                    setLeads(revertedLeads);
                    // Optionally show an error message to the user
                    // alert('Failed to log call attempt. Please try again.'); 
                    // --- End Revert ---
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </a>
            )}
            {lead.phone || '—'}
          </div>
        );
      case 'secondary_phone':
        return (
          <div className="text-sm text-gray-500 flex items-center">
            {lead.secondary_phone && (
              <a 
                href={`tel:${lead.secondary_phone}`} 
                className="text-blue-500 hover:text-blue-700 mr-2" 
                title="Call secondary number (will increment call attempts)"
                onClick={async (e) => {
                  // Don't prevent default so the tel: link still works
                  
                  // Set this lead as the last called lead (for highlighting)
                  setLastCalledLeadId(lead.id);
                  
                  // Store original lead state for potential revert
                  const originalLead = { ...lead }; 

                  try {
                    // Only increment if not already at max
                    if ((lead.call_attempts || 0) < 10) {
                      const token = localStorage.getItem('token');
                      
                      // --- Optimistic UI Update ---
                      const optimisticDate = new Date().toISOString();
                      const optimisticLeads = leads.map(l =>
                        l.id === lead.id
                          ? {
                              ...l,
                              call_attempts: (l.call_attempts || 0) + 1, // Optimistically increment
                              last_worked_date: optimisticDate // Optimistically set date
                            }
                          : l
                      );
                      setLeads(optimisticLeads);
                      // --- End Optimistic UI Update ---

                      const res = await axios.patch(
                        `/api/leads/${lead.id}/call-attempts`,
                        { increment: true },
                        {
                          headers: {
                            'Content-Type': 'application/json',
                            'x-auth-token': token
                          }
                        }
                      );
                      
                      // We know the PATCH succeeded, so the call_attempts increment is confirmed.
                      // The API response (res.data) might not contain the full updated lead or the last_worked_date.
                      
                      const confirmedAttempts = (originalLead.call_attempts || 0) + 1;
                      let confirmedStatus = originalLead.status;
                      let statusUpdated = false;

                      // Check if we need to update status to "unreachable" (if call attempts reaches 7)
                      if (confirmedAttempts >= 7 && originalLead.status !== 'unreachable') {
                        confirmedStatus = 'unreachable';
                        statusUpdated = true;
                        // Update the lead status to unreachable via a separate API call
                        // We assume this PUT call also updates last_worked_date on the backend
                        await axios.put(
                          `/api/leads/${lead.id}`,
                          { status: 'unreachable' }, // Only send the status change
                          { /* headers */ }
                        ).catch(statusErr => {
                           // Handle potential error during status update if necessary
                           console.error('Error updating lead status to unreachable:', statusErr);
                           // Decide if we should revert status optimistically? For now, we'll proceed.
                        });
                      }
                      
                      // Construct the final state update based on confirmed changes & optimistic date
                      const finalUpdatedLeads = leads.map(l => {
                        if (l.id === lead.id) {
                          // Use the original lead data as base, apply confirmed changes and optimistic date
                          return { 
                            ...originalLead, // Start with original data
                            call_attempts: confirmedAttempts, // Apply confirmed attempts
                            status: confirmedStatus, // Apply confirmed status (might be original or 'unreachable')
                            last_worked_date: optimisticDate // Keep the optimistic date
                          };
                        }
                        return l;
                      });
                      setLeads(finalUpdatedLeads); // Apply the final calculated state
                    }
                  } catch (err) {
                    console.error('Error updating call attempts:', err);
                    // --- Revert Optimistic Update on Error ---
                    const revertedLeads = leads.map(l => 
                      l.id === lead.id ? originalLead : l // Restore original lead data
                    );
                    setLeads(revertedLeads);
                    // Optionally show an error message to the user
                    // alert('Failed to log call attempt. Please try again.'); 
                    // --- End Revert ---
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </a>
            )}
            {lead.secondary_phone || '—'}
          </div>
        );
      case 'call_attempts':
        return (
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center font-medium text-blue-700">
                {lead.call_attempts || 0}
              </div>
              <span className="text-xs text-gray-500">/ 10</span>
            </div>
            <div className="flex space-x-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Only decrement if greater than 0
                  if ((lead.call_attempts || 0) > 0) {
                    const token = localStorage.getItem('token');
                    axios.patch(
                      `/api/leads/${lead.id}/call-attempts`,
                      { increment: false },
                      {
                        headers: {
                          'Content-Type': 'application/json',
                          'x-auth-token': token
                        }
                      }
                    ).then(res => {
                      // Update leads in the parent component
                      // Update leads in the parent component
                      const updatedLeads = leads.map(l => 
                        l.id === lead.id ? {...l, call_attempts: (l.call_attempts || 0) - 1} : l
                      );
                      setLeads(updatedLeads);
                    }).catch(err => {
                      console.error('Error updating call attempts:', err);
                    });
                  }
                }}
                className={`p-1 rounded-full ${
                  (lead.call_attempts || 0) > 0
                    ? 'text-red-600 hover:bg-red-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                disabled={(lead.call_attempts || 0) === 0}
                title="Decrement call attempts"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Only increment if less than 10
                  if ((lead.call_attempts || 0) < 10) {
                    const token = localStorage.getItem('token');
                    axios.patch(
                      `/api/leads/${lead.id}/call-attempts`,
                      { increment: true },
                      {
                        headers: {
                          'Content-Type': 'application/json',
                          'x-auth-token': token
                        }
                      }
                    ).then(res => {
                      // Check if status should update to unreachable
                      let updatedLead = {...lead, call_attempts: (lead.call_attempts || 0) + 1};
                      
                      // If call attempts reaches 7 and status isn't already unreachable
                      if (updatedLead.call_attempts >= 7 && updatedLead.status !== 'unreachable') {
                        axios.put(
                          `/api/leads/${lead.id}`,
                          { ...updatedLead, status: 'unreachable' },
                          {
                            headers: {
                              'Content-Type': 'application/json',
                              'x-auth-token': token
                            }
                          }
                        ).then(statusRes => {
                          updatedLead = statusRes.data;
                          // Update leads in parent component
                          const updatedLeads = leads.map(l => 
                            l.id === lead.id ? updatedLead : l
                          );
                          setLeads(updatedLeads);
                        });
                      } else {
                        // Update leads in parent component
                        const updatedLeads = leads.map(l => 
                          l.id === lead.id ? updatedLead : l
                        );
                        setLeads(updatedLeads);
                      }
                    }).catch(err => {
                      console.error('Error updating call attempts:', err);
                    });
                  }
                }}
                className={`p-1 rounded-full ${
                  (lead.call_attempts || 0) < 10
                    ? 'text-green-600 hover:bg-green-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                disabled={(lead.call_attempts || 0) === 10}
                title="Increment call attempts"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        );
      case 'status':
        return (
          <div className="text-sm cursor-pointer inline-flex items-center relative">
            <select
              className="absolute opacity-0 top-0 left-0 w-full h-full cursor-pointer z-10"
              value={lead.status}
              onChange={(e) => {
                const newStatus = e.target.value;
                // Update the lead status via API
                const leadToUpdate = leads.find(l => l.id === lead.id);
                if (leadToUpdate) {
                  const token = localStorage.getItem('token');
                  // Create a simpler object with just the required fields
                  const updateData = {
                    id: leadToUpdate.id,
                    name: leadToUpdate.name || '',
                    email: leadToUpdate.email || '',
                    status: newStatus
                  };
                  
                  console.log('Updating lead with data:', updateData);
                  
                  axios.put(
                    `/api/leads/${lead.id}`,
                    updateData,
                    {
                      headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': token
                      }
                    }
                  ).then(res => {
                    console.log('Lead update successful:', res.data);
                    // Update leads in the parent component
                    const updatedLeads = leads.map(l => 
                      l.id === lead.id ? res.data : l
                    );
                    setLeads(updatedLeads);
                  }).catch(err => {
                    console.error('Error updating lead status:', err);
                    console.error('Error details:', err.response?.data);
                  });
                }
              }}
            >
              <option value="new">New</option>
              <option value="connected">Connected</option>
              <option value="HOT">HOT 🔥</option>
              <option value="unreachable">Unreachable</option>
              <option value="disqualified">Disqualified</option>
              <option value="converted">Converted</option>
            </select>
            <div className="inline-flex items-center z-0">
              <span 
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  {
                  'new': 'bg-blue-100 text-blue-800',
                  'connected': 'bg-yellow-100 text-yellow-800',
                  'HOT': 'bg-red-100 text-red-800',
                  'disqualified': 'bg-gray-100 text-gray-800',
                  'converted': 'bg-green-100 text-green-800',
                  'unreachable': 'bg-gray-100 text-gray-800'
                }[lead.status] || 'bg-gray-100 text-gray-800'}`}
              >
                {lead.status === 'HOT' ? 'HOT 🔥' : lead.status}
              </span>
              <svg className="ml-1 h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        );
      case 'country':
        return (
          <div className="text-sm text-gray-500">{lead.country || '—'}</div>
        );
      case 'deals':
        return (
          <div className="text-sm text-center">
            {dealCounts[lead.id] ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {dealCounts[lead.id]}
              </span>
            ) : '—'}
          </div>
        );
      case 'original_source':
        return (
          <div className="text-sm text-gray-500 truncate max-w-[250px]" title={lead.original_source}>
            {lead.original_source || '—'}
          </div>
        );
      case 'latest_source':
        return (
          <div className="text-sm text-gray-500 truncate max-w-[250px]" title={lead.latest_source}>
            {lead.latest_source || '—'}
          </div>
        );
      case 'assigned_to':
        return (
          <div className="text-sm text-gray-500">
            {lead.assigned_to 
              ? users.find(user => user.id === lead.assigned_to)?.name || 'Unknown'
              : '—'}
          </div>
        );
      case 'created_at':
        return (
          <div className="text-sm text-gray-500">
            {lead.created_at ? new Date(lead.created_at).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
        );
      case 'reconversion_date':
        return (
          <div className="text-sm text-gray-500">
            {lead.reconversion_date ? new Date(lead.reconversion_date).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
        );
      case 'last_conversion_date':
        return (
          <div className="text-sm text-gray-500">
            {lead.last_conversion_date ? new Date(lead.last_conversion_date).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
        );
      case 'last_worked_date':
        return (
          <div className="text-sm text-gray-500">
            {lead.last_worked_date ? new Date(lead.last_worked_date).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </div>
        );
      default:
        return null;
    }
  };

  // Reset columns function to handle users that already have saved smaller column widths
  const resetColumnsToWider = () => {
    setColumns(defaultColumns);
    localStorage.setItem('leadsTableColumns', JSON.stringify(defaultColumns));
  };

  // Use an effect to check if we need to reset column widths for existing users
  useEffect(() => {
    // Only check once when the component mounts
    const savedColumns = localStorage.getItem('leadsTableColumns');
    if (savedColumns) {
      const parsedColumns = JSON.parse(savedColumns);
      // Check if total width of visible columns is significantly smaller than our new defaults
      const totalSavedWidth = parsedColumns.filter(col => col.visible).reduce((sum, col) => sum + col.width, 0);
      const totalDefaultWidth = defaultColumns.filter(col => col.visible).reduce((sum, col) => sum + col.width, 0);
      
      // If the saved width is more than 25% smaller than our new default widths, reset to new defaults
      if (totalSavedWidth < totalDefaultWidth * 0.75) {
        resetColumnsToWider();
      }
    }
  }, []);

  // Prepare leads data with isLastCalled property for SharedTable
  const enhancedLeads = leads.map(lead => ({
    ...lead,
    // Add isLastCalled property for row highlighting
    isLastCalled: lead.id === lastCalledLeadId
  }));

  return (
    <SharedTable
      data={enhancedLeads}
      columns={columns}
      selectedItems={selectedLeads}
      onSelectItem={handleLeadSelection}
      selectAll={selectAll}
      onSelectAll={handleSelectAll}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      renderCell={renderCell}
      loading={loading}
      emptyMessage="No leads found. Create your first lead or import leads to get started."
      resizable={true}
      onColumnsChange={setColumns}
      showColumnManagement={showColumnModal}
      onToggleColumnManagement={setShowColumnModal}
      pagination={pagination}
      onPageChange={onPageChange}
      onLimitChange={onLimitChange} // <<< Pass onLimitChange prop down
      // fetchLeads={fetchLeads} // Removed fetchLeads prop
      tableHeight="calc(100vh - 200px)"
      onResetColumns={resetColumnsToWider}
    />
  );
};

export default LeadsTableWithShared;
